import type {AxiosError} from 'axios';
import {isEmpty} from 'lodash-es';
import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import Spinner from '@/assets/svg/Spinner';
import {Badge, Button} from '@/components';
import {Input} from '@/components/common/Input';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {usePromotions} from '@/pages/Promotions/usePromotions';
import type {AfriexTier} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';

import UserCardListTable from './UserCardListTable';
import UserTierTable from './UserTierTable';
import useUserDetails from './useUserDetails';
import VirtualCardInfo from './VirtualCardInfo';

type TierInformationProps = {
  /** `TierInfo['id']` off the person; absent until a tier is assigned. */
  id?: string;
  transactionCount?: number;
  userId: string;
  refetchUser: () => Promise<void>;
};

const TierInformation = ({
  id,
  transactionCount,
  userId,
  refetchUser,
}: TierInformationProps) => {
  const {
    getPromotion,
    updatePromotionForUser,
    deletePromotionForUser,
    isLoading,
  } = usePromotions();
  const [promotion, setPromotion] = useState<AfriexTier | null>(null);
  const [tierIdInput, setTierIdInput] = useState<string>('');
  const {
    activeCards,
    updateCardBankRegulatedStatus,
    virtualCard,
    virtualCardBalance,
  } = useUserDetails(userId);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTierIdInput(event.target.value);
  };
  const handleUpdatePromotion = async () => {
    if (!tierIdInput.trim()) {
      toast.warn('Please provide a Tier ID.');
      return;
    }

    try {
      await updatePromotionForUser(userId, tierIdInput);
      await refetchUser();
      toast.success(`User Tier ${promotion ? 'updated' : 'added'}`);
      setTierIdInput('');
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 404) {
        toast.error('Tier not found.');
        return;
      }
      toast.error(getErrorMessage(error, 'Error updating tier for user.'));
    }
  };

  const handleDeletePromotion = async () => {
    try {
      await deletePromotionForUser(userId);
      await refetchUser();
      toast.success('User Tier deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error deleting tier for user.'));
    }
  };

  const fetchPromotion = useCallback(
    (id?: string) => {
      if (id) {
        getPromotion(id)
          .then((promotion) => {
            // `getPromotion` (backed by `api/tiers.ts`'s `getTier`) is typed
            // as the Promotions page's own `PromotionTier`, whose `type` is a
            // bare `string`; this table's `AfriexTier` narrows the same field
            // to `TierTypes`. Neither `UserTierTable` nor this component reads
            // `.type`, so the cast is a same-shape relabel, not a behaviour
            // change.
            setPromotion(promotion as AfriexTier);
          })
          // `usePromotions.getPromotion` does not catch, so nothing else can
          // handle this. A failed lookup leaves `promotion` null and the tier
          // table hidden, which is exactly what happens today; `logger` is
          // dev-only, so production stays silent as before. Surfacing this to
          // the admin would be a behaviour change.
          .catch((error) => {
            logger.error('Failed to fetch the user tier:', error);
          });
      }
    },
    [id],
  );

  useEffect(() => {
    // fetchPromotion is synchronous; the promise chain it starts already ends
    // in a catch, so nothing here can throw.
    fetchPromotion(id);
  }, [fetchPromotion, id]);

  const hasActiveCards = activeCards?.length > 0;
  const hasVirtualCard = !isEmpty(virtualCard);
  const walletsUsed = new Set(
    activeCards.map((t) =>
      'platformSource' in t.info ? t.info.platformSource : undefined,
    ),
  );

  const hasApplePay = walletsUsed.has('Apple Pay');
  const hasGooglePay = walletsUsed.has('Google Pay');

  return (
    <div className="px-3 pt-3 mt-8">
      <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {'Promos & Tiers'.toLocaleUpperCase()}
      </h3>
      <hr className="mb-5 dark:border-dark-borderMid" />

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-200">
          Tiers / Promotions
        </h3>

        <div className={'max-w'}>
          {promotion && id && (
            <UserTierTable
              tierInfo={promotion}
              transactionCount={transactionCount}
            />
          )}

          <PermissionsProvider
            permission={AfriexAdminPermission.UserTierManage}
          >
            <div className="update-tier-form flex flex-col mt-3 md:mt-5">
              <h1 className="text-sm font-semibold mb-2 text-gray-600 dark:text-gray-200">
                {id ? 'Modify / Delete ' : 'Add '} Tier
              </h1>

              <div className="flex flex-col md:flex-row gap-2 md:gap-5">
                <Input
                  type="text"
                  value={tierIdInput}
                  onChange={handleInputChange}
                  placeholder="Tier ID"
                  className="w-full md:w-60 dark:!bg-dark-inputBg"
                />

                <div className="flex gap-3">
                  <Button
                    variant="solid"
                    colorScheme="cyan"
                    onClick={handleUpdatePromotion}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner />
                    ) : (
                      (promotion ? 'Update' : 'Add User to') + ' Tier'
                    )}
                  </Button>

                  {promotion && id && (
                    <Button
                      variant="solid"
                      colorScheme="red"
                      onClick={handleDeletePromotion}
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinner /> : 'Delete Tier'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </PermissionsProvider>
        </div>

        <div className="mt-3">
          <div className="flex gap-2 items-center mb-2">
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-200">
              Active Debit Cards
            </h2>

            {hasApplePay && (
              <Badge label="Apple Pay" colorScheme="successful" />
            )}

            {hasGooglePay && (
              <Badge label="Google Pay" colorScheme="successful" />
            )}
          </div>

          {hasActiveCards ? (
            <UserCardListTable
              activeCardList={activeCards}
              userId={userId}
              updateCardBankRegulatedStatus={updateCardBankRegulatedStatus}
            />
          ) : (
            <p className="text-gray-500 dark:text-gray-400">
              No active debit card was found for this user
            </p>
          )}
        </div>

        <div className="mt-3">
          <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-200 mb-2">
            Virtual Card
          </h2>

          {hasVirtualCard ? (
            <VirtualCardInfo
              cardData={virtualCard}
              virtualCardBalance={virtualCardBalance || '0'}
            />
          ) : (
            <p className="text-gray-500 dark:text-gray-400">
              No virtual card was found for this user
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TierInformation;
